import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Divider, Tooltip } from '@material-ui/core';
import * as actions from '../../../../../store/index';
import { useStyles } from './DiagnoseOverlays.style';
import { useAppliedEvaluation } from '../../../../../helpers/hooks/useEvaluation';
import { updateNoTypeRectangles } from '../../../../../store';
import { diagnosticsClassColor } from '../../../../../helpers/manageColors';

// Import simplified helpers
import {
  createMemoizedOverlayTypes,
  drawSingleBoxOnCanvas,
  drawContourOnCanvas,
  initializeOffscreenCanvas,
  cleanupCanvasResources,
  generateUniqueID,
} from './DiagnoseOverlaysHelpers/overlayTypesConfig';
import {
  handleDeleteButtonClick,
  handleReassignButtonClick,
} from './DiagnoseOverlaysHelpers/DiagnoseOverlaysHelpers';
import ContextMenu from './DiagnoseOverlaysHelpers/ContextMenu';
import { drawSmoothShapeCatmullRom } from './DiagnoseOverlaysHelpers/CanvasDrawingHelpers';
import { useMissedFindings } from '../../../../../helpers/hooks/useMissedFindings';
import { isMissedFinding, getOriginLabelKeys } from '../../../../../helpers/diagnostics/detectionOrigin';

const DiagnoseOverlays = ({ proj_name, canvasRef, imgSize, onWheel, onMouseMove }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  // Refs for drawing state
  const isDrawingRef = useRef(false);
  const lastDrawTimeRef = useRef(0);
  const offscreenCanvasRef = useRef(null);
  const pathPool = useRef(new Map());
  const isMountedRef = useRef(true);

  // Redux state
  const { applied_microcalc, applied_opacities, shown_microcalc, shown_opacities, exam_id } = useSelector(
    (state) => state.results,
  );

  const {
    savingPending,
    customMicrocalc,
    customOpacities,
    diagnosticsInRevisionMode,
    singleBoxHover,
    targetLesionId,
    draggingLesionId,
    noTypeRectangles,
    justEditedRows,
  } = useSelector((state) => state.labeling);

  const { microcalcOverlaysToShow, opacitiesOverlaysToShow } = useSelector((state) => state.images);
  const { isVisible } = useMissedFindings();
  const { tabsIndex, zoomedProjection } = useSelector((state) => state.gui);
  const { user_settings, premium_features } = useSelector((state) => state.global_settings);
  const sopInstanceUID = useSelector((state) => state.results.projections?.[proj_name]?.uid || null);

  // Local state - simplified
  const [tempDrawingRectangle, setTempDrawingRectangle] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedOption, setSelectedOption] = useState({
    option: '',
    type: '',
    rectId: null,
  });
  const [isHovered, setIsHovered] = useState(false);

  const { diagnostics_microcalc_module, diagnostics_opacities_module } = premium_features;
  const { presentation_lesions } = user_settings;
  const microcalcShown = shown_microcalc || applied_microcalc;
  const opacitiesShown = shown_opacities || applied_opacities;

  // Initialize offscreen canvas
  useEffect(() => {
    return initializeOffscreenCanvas(imgSize, offscreenCanvasRef);
  }, [imgSize]);

  // Remove canvas key approach - it's causing too many re-renders

  // Get evaluation results
  const resultMicrocalcEvaluation = useAppliedEvaluation(
    'microcalc',
    microcalcShown,
    diagnosticsInRevisionMode,
  );
  const resultOpacitiesEvaluation = useAppliedEvaluation(
    'opacities',
    opacitiesShown,
    diagnosticsInRevisionMode,
  );

  // Memoized overlay types - simplified
  const customOverlayTypes = useMemo(
    () => createMemoizedOverlayTypes(microcalcOverlaysToShow, opacitiesOverlaysToShow, proj_name, theme),
    [microcalcOverlaysToShow, opacitiesOverlaysToShow, proj_name, theme],
  );

  const isFindingHovered = useCallback(
    (finding, findingClass, findingID) => {
      const isLocalBoxHover = isHovered === findingID;
      const isDirectHoverMatch =
        (!!singleBoxHover?.findingID && singleBoxHover?.findingID === finding?.finding_id) ||
        (!!singleBoxHover?.lesionID &&
          !!finding?.object_id &&
          singleBoxHover?.lesionID === finding?.object_id);

      const findingProjection = Array.isArray(finding?.projection)
        ? finding?.projection[0]
        : finding?.projection || proj_name;
      const findingBreast =
        findingProjection === 'rcc' || findingProjection === 'rmlo'
          ? 'right'
          : findingProjection === 'lcc' || findingProjection === 'lmlo'
            ? 'left'
            : null;

      const isClusterHoverMatch =
        !!singleBoxHover?.lesionID &&
        !!singleBoxHover?.cluster?.findingClass &&
        !!singleBoxHover?.cluster?.breast &&
        singleBoxHover?.cluster?.findingClass === findingClass &&
        singleBoxHover?.cluster?.breast === findingBreast;

      return isLocalBoxHover || isDirectHoverMatch || isClusterHoverMatch;
    },

    [isHovered, singleBoxHover, proj_name],
  );

  const visibleFindings = useMemo(() => {
    const findings = [];
    const projectionOpacities = resultOpacitiesEvaluation?.[proj_name] || {};

    Object.values(customOverlayTypes).forEach((overlayType) => {
      if (!overlayType.show) return;

      const { lesionModule, type, color, boxShadow } = overlayType;

      const data =
        lesionModule === 'microcalc'
          ? resultMicrocalcEvaluation?.[proj_name]?.[type]
          : resultOpacitiesEvaluation?.[proj_name]?.[type];

      if (!Array.isArray(data)) return;

      data.forEach((finding, index) => {
        if (!isVisible(finding)) return;

        findings.push({
          finding,
          overlayType,
          findingIndex: index,
        });
      });
    });

    return findings;
  }, [customOverlayTypes, resultMicrocalcEvaluation, resultOpacitiesEvaluation, proj_name, isVisible]);

  // Draw overlays - simplified
  useEffect(() => {
    if (!canvasRef.current || !offscreenCanvasRef.current) return;
    if (!imgSize) return;

    const [w, h] = imgSize;

    if (!w || !h) return;

    const canvas = canvasRef.current;
    const offscreen = offscreenCanvasRef.current;

    const ctx = canvas.getContext('2d');
    const offCtx = offscreen.getContext('2d');

    canvas.width = w;
    canvas.height = h;
    offscreen.width = w;
    offscreen.height = h;

    offCtx.clearRect(0, 0, w, h);

    visibleFindings.forEach(({ finding, overlayType }) => {
      const { lesionModule, color } = overlayType;
      const missed = isMissedFinding(finding);

      if (presentation_lesions?.includes('contour') && Array.isArray(finding.contours)) {
        finding.contours.forEach((contour) => {
          drawContourOnCanvas(contour, offCtx, color, lesionModule, imgSize, missed);
        });
      }

      if (presentation_lesions?.includes('overlay') && Array.isArray(finding.contours)) {
        finding.contours.forEach((contour) => {
          drawSmoothShapeCatmullRom(contour, offCtx, color, false, true, imgSize, pathPool);
        });
      }
    });

    if (offscreen.width > 0 && offscreen.height > 0) {
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(offscreen, 0, 0);
    }
  }, [visibleFindings, imgSize, presentation_lesions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanupCanvasResources(isDrawingRef, offscreenCanvasRef, pathPool);
    };
  }, []);

  // Event handlers - simplified
  const handleBoxClick = useCallback(() => {
    if (!zoomedProjection) {
      dispatch(actions.setZoomedProjection(proj_name));
    } else {
      dispatch(actions.setZoomedProjection(null));
    }
    dispatch(actions.resetHoverState());
  }, [zoomedProjection, proj_name, dispatch]);

  const handleMouseEnterBox = useCallback((findingID, lesionID) => {
    setIsHovered(findingID);
    dispatch(actions.setSingleBoxHover({ findingID: findingID, lesionID: lesionID }));
  }, []);

  const handleMouseLeaveBox = useCallback(() => {
    setIsHovered(null);
    dispatch(actions.resetHoverState());
  }, []);

  // Drawing handlers - simplified for single box
  const handleStartDrawingRectangle = useCallback(
    (event) => {
      if (!canvasRef || !canvasRef.current || !diagnosticsInRevisionMode) return;

      // Only start drawing on left mouse button (button 0), not right-click (button 2)
      if (event.button !== 0) return;

      // Don't start a new rectangle if we're already drawing one
      if (tempDrawingRectangle?.mode === 'drawing') return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      // Start new drawing rectangle
      setTempDrawingRectangle({
        mode: 'drawing',
        startPoint: { x, y },
        endPoint: { x, y },
      });
    },
    [canvasRef, diagnosticsInRevisionMode, tempDrawingRectangle],
  );

  const handleDrawingRect = useCallback(
    (event) => {
      if (!canvasRef || !canvasRef.current || !tempDrawingRectangle?.mode === 'drawing') return;

      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      setTempDrawingRectangle((prevDrawing) => ({
        ...prevDrawing,
        endPoint: { x, y },
      }));
    },
    [canvasRef, tempDrawingRectangle],
  );

  const handleStopDrawingRectangle = useCallback(() => {
    if (!diagnosticsInRevisionMode || !tempDrawingRectangle) {
      return;
    }

    if (tempDrawingRectangle?.mode === 'drawing') {
      const newRectangle = {
        id: generateUniqueID(proj_name, noTypeRectangles?.[proj_name]),
        box: [
          tempDrawingRectangle?.startPoint?.x,
          tempDrawingRectangle?.startPoint?.y,
          tempDrawingRectangle?.endPoint?.x - tempDrawingRectangle?.startPoint?.x,
          tempDrawingRectangle?.endPoint?.y - tempDrawingRectangle?.startPoint?.y,
        ],
        color: 'cyan',
        score: '100%',
      };

      // Replace existing rectangles with new one (single box only)
      dispatch(updateNoTypeRectangles(proj_name, [newRectangle]));

      // Show context menu for the new rectangle
      const centerX = (tempDrawingRectangle.startPoint.x + tempDrawingRectangle.endPoint.x) / 2;
      const centerY = (tempDrawingRectangle.startPoint.y + tempDrawingRectangle.endPoint.y) / 2;

      // Get canvas position
      if (canvasRef?.current) {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const screenX = rect.left + centerX * rect.width;
        const screenY = rect.top + centerY * rect.height;

        setTimeout(() => {
          setContextMenu({
            mouseX: screenX + 20,
            mouseY: screenY + 10,
            rectangleId: newRectangle.id,
            rectangleProjection: proj_name,
            SOPInstanceUID: sopInstanceUID,
          });
        }, 600);
      }
    }
    setTempDrawingRectangle(null);
  }, [diagnosticsInRevisionMode, tempDrawingRectangle, proj_name, noTypeRectangles, dispatch, canvasRef]);

  // Context menu handlers - simplified
  const handleContextMenu = useCallback(
    (e, findingId, lesionType, lesionModule, findingIndex) => {
      e.preventDefault();
      setContextMenu({
        mouseX: e.clientX - 2,
        mouseY: e.clientY - 4,
        rectangleId: findingId,
        rectangleProjection: proj_name,
        rectangleType: lesionModule,
        rectangleClass: lesionType,
        findingIndex: findingIndex,
      });
    },
    [proj_name],
  );

  // Close menu function
  const closeMenu = useCallback(() => {
    setContextMenu(null);
    setSelectedOption({
      option: '',
      type: '',
      rectId: null,
    });
  }, []);

  // Handle menu item click
  const handleMenuItemClickWrapper = useCallback(
    (action) => {
      if (action === 'delete') {
        handleDeleteButtonClick(
          exam_id,
          contextMenu,
          setContextMenu,
          customMicrocalc,
          customOpacities,
          noTypeRectangles,
          dispatch,
          t,
        );
      } else {
        const resultFindings =
          contextMenu.rectangleType === 'microcalc' ? resultMicrocalcEvaluation : resultOpacitiesEvaluation;

        handleReassignButtonClick(
          exam_id,
          selectedOption?.type,
          action,
          contextMenu,
          resultFindings,
          customMicrocalc,
          customOpacities,
          noTypeRectangles,
          setContextMenu,
          setSelectedOption,
          dispatch,
          t,
        );
      }

      closeMenu();
    },
    [
      contextMenu,
      noTypeRectangles,
      handleDeleteButtonClick,
      handleReassignButtonClick,
      closeMenu,
      selectedOption,
    ],
  );

  const getProbTextLeft = useCallback((proj_name, x, w) => {
    if (proj_name?.includes('r')) {
      return x > 0.8 ? `calc(${100 * (x + w)}% - 20px)` : `calc(${100 * x}%)`;
    }
    return x > 0.8 ? `calc(${100 * (x + w)}% - 10px)` : `calc(${100 * x}%)`;
  }, []);

  // Render bounding boxes
  const boundingBoxes = useMemo(() => {
    return visibleFindings
      .filter(({ finding }) => Array.isArray(finding.box) && finding.box.length === 4)
      .map(({ finding, overlayType, findingIndex }) => {
        const { lesionModule, type, color, boxShadow } = overlayType;
        const [x, y, w, h] = finding.box;

        const findingID = finding?.finding_id;
        const findingClass = type;
        const matchedLesionId = finding?.object_id;

        return {
          finding,
          findingID,
          findingClass,
          matchedLesionId,
          lesionModule,
          findingIndex,
          type,
          color,
          boxShadow,
          x,
          y,
          w,
          h,
        };
      });
  }, [visibleFindings, proj_name]);

  // Render unassigned boxes
  const unassignedBoxes = useMemo(() => {
    const rectangles = noTypeRectangles?.[proj_name];
    if (!rectangles || rectangles.length === 0) return null;

    // Only show the first rectangle (single box only)
    const rectangle = rectangles[0];
    const [x, y, w, h] = rectangle.box;

    return (
      <div
        key={rectangle.id}
        style={{
          zIndex: 2500,
          left: `${100 * x}%`,
          top: `${100 * y}%`,
          width: `${100 * w}%`,
          height: `${100 * h}%`,
          border: rectangle.color ? `2px solid ${rectangle.color}` : '2px solid cyan',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          position: 'absolute',
          cursor: 'pointer',
        }}
        onMouseOver={() => setIsHovered(rectangle.id)}
        onMouseLeave={() => setIsHovered(null)}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
            rectangleId: rectangle.id,
            rectangleProjection: proj_name,
          });
        }}
      />
    );
  }, [noTypeRectangles, proj_name]);

  return (
    <div
      className={classes.container_overlay}
      onWheel={onWheel}
      onContextMenu={(e) => e.preventDefault()}
      onMouseMove={onMouseMove}
      onMouseLeave={() => tempDrawingRectangle?.mode === 'drawing' && setTempDrawingRectangle(null)}
      style={{
        cursor:
          tabsIndex === 2 && diagnosticsInRevisionMode
            ? tempDrawingRectangle?.mode === 'drawing'
              ? 'e-resize'
              : 'crosshair'
            : 'inherit',
      }}>
      {/* Temporary drawing rectangle */}
      {tempDrawingRectangle && tempDrawingRectangle?.mode === 'drawing' && (
        <div
          style={{
            zIndex: 2500,
            left: `${100 * tempDrawingRectangle?.startPoint?.x}%`,
            top: `${100 * tempDrawingRectangle?.startPoint?.y}%`,
            width: `${100 * (tempDrawingRectangle?.endPoint.x - tempDrawingRectangle?.startPoint.x)}%`,
            height: `${100 * (tempDrawingRectangle?.endPoint.y - tempDrawingRectangle?.startPoint.y)}%`,
            border: '2px solid cyan',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            position: 'absolute',
          }}
        />
      )}

      {/* Rendered bounding boxes */}
      <>
        {boundingBoxes.map((box, index) => {
          const {
            finding,
            findingID,
            findingClass,
            matchedLesionId,
            lesionModule,
            type,
            color,
            boxShadow,
            x,
            y,
            w,
            h,
            findingIndex,
          } = box;

          const isPrimaryHover = isFindingHovered(finding, findingClass, findingID);
          // Secondary hover: lesionID match
          const isSecondaryHover =
            singleBoxHover?.lesionID && singleBoxHover?.lesionID === matchedLesionId && matchedLesionId;

          // Tertiary hover: findingID matches lesionID (for related findings)
          const isTertiaryHover =
            singleBoxHover?.findingID && singleBoxHover?.findingID === matchedLesionId && matchedLesionId;

          const shouldScale = targetLesionId === findingID;
          const formattedWidth = finding?.size?.[0]?.toFixed(2);
          const formattedHeight = finding?.size?.[1]?.toFixed(2);
          const missed = isMissedFinding(finding);
          const originKeys = missed ? getOriginLabelKeys(finding.detection_origin) : null;

          const hasSlice = typeof finding?.slice === 'number';
          const isTomo = finding?.found_on === '3d' || finding?.found_on === '2d_3d';

          // Create tooltip content
          const tooltipContent = (
            <>
              {missed && (
                <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                  {t(originKeys.long)}
                </Typography>
              )}
              {finding?.detection_origin === 'second_capture' && (
                <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                  {t('projections.from_second_capture')}
                </Typography>
              )}
              <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                {`${t(`diagnose.type_${lesionModule}`)} - ${t(`revision.${type}`)}`}
              </Typography>
              <div>
                <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                  {`ID: ${findingID || findingIndex}`}
                </Typography>
              </div>
              {isTomo ? (
                <>
                  <Divider style={{ marginBottom: '4px' }} />
                  <Typography className={classes.tooltip_text}>
                    {finding?.found_on === '2d_3d'
                      ? t('projections.found_on_2d_3d')
                      : finding?.found_on === '3d'
                        ? t('projections.found_on_3d')
                        : ''}
                  </Typography>
                  {hasSlice && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.found_on_slice')}: #{finding?.slice ?? '0'}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography className={classes.tooltip_text}>{t('projections.found_on_2d')}</Typography>
              )}
              {tabsIndex !== 2 && (
                <>
                  <Divider style={{ marginBottom: '4px' }} />
                  {formattedWidth && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.lesion_w', { lesionWidth: `${formattedWidth || ' N/A '} ` })}
                    </Typography>
                  )}
                  {formattedHeight && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.lesion_h', { lesionHeight: `${formattedHeight || ' N/A '} ` })}
                    </Typography>
                  )}
                  {!!finding?.location && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.location')}: {t(`diagnose.${finding?.location}`)}
                    </Typography>
                  )}
                  {!!finding?.depth && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.depth')}: {t(`diagnose.${finding?.depth}`)}
                    </Typography>
                  )}
                </>
              )}

              {(finding?.distance_nipple_mm || finding?.distance_chest_wall_mm) && (
                <>
                  <Divider style={{ marginBottom: '4px' }} />
                  {!!finding?.distance_nipple_mm && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.lesion_distance_nipple', {
                        distance: `${parseFloat(finding?.distance_nipple_mm).toFixed(2)}` || '-',
                      })}
                    </Typography>
                  )}
                  {!!finding?.distance_chest_wall_mm && (
                    <Typography className={classes.tooltip_text}>
                      {t('projections.lesion_distance_wall', {
                        distance: `${parseFloat(finding?.distance_chest_wall_mm).toFixed(2)}` || '-',
                      })}
                    </Typography>
                  )}
                </>
              )}

              {finding?.match && (
                <>
                  <Divider style={{ marginBottom: '4px' }} />
                  <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                    {`${t('diagnose.matched_lesion')} ID: ${matchedLesionId || findingID}`}
                  </Typography>
                </>
              )}
            </>
          );

          return (
            <div key={findingID}>
              {!draggingLesionId && !shouldScale && (
                <p
                  style={{
                    left: x > 0.4 ? `calc(${100 * (x + w)}% - 58px)` : `calc(${100 * x}%)`,
                    top: y <= 0.03 ? `calc(${100 * y + 100 * h}%)` : `calc(${100 * y - 7}%)`,
                    fontSize: theme.fonts.responsiveSmall,
                    color: isMissedFinding(finding)
                      ? `${theme.palette.diagnosticsClasses.priorFinding}`
                      : color || 'cyan',
                    margin: y <= 0.03 && '2px',
                    position: 'absolute',
                    zIndex: 1600,
                  }}>
                  {t(`revision.${findingClass}`)} {missed ? `(${t(originKeys.short)}) ` : ''}
                  {finding?.detection_origin === 'second_capture'
                    ? `(${t('projections.from_second_capture_short')}) `
                    : ''}
                  {finding.severity_change_latest
                    ? finding.severity_change_latest > 0
                      ? '↑'
                      : finding.severity_change_latest < 0
                        ? '↓'
                        : '↔'
                    : ''}
                </p>
              )}

              {/* <p
                style={{
                  left: x > 0.4 ? `calc(${100 * (x + w)}% - 58px)` : `calc(${100 * x}%)`,
                  bottom: y <= 0.03 ? `calc(${100 * y - 100 * h}%)` : `calc(${100 * y + 17}%)`,
                  fontSize: '9px',
                  position: 'absolute',
                  zIndex: 1600,
                  color: 'white',
                  backgroundColor: 'black',
                }}>
                ID: {findingID} / {matchedLesionId && `[${matchedLesionId}]`}
              </p> */}

              <Tooltip
                title={tooltipContent}
                open={
                  (!zoomedProjection || zoomedProjection === proj_name) &&
                  (isPrimaryHover || isSecondaryHover || isTertiaryHover)
                    ? true
                    : false
                }
                placement='top'
                arrow>
                <div
                  onClick={handleBoxClick}
                  onMouseEnter={() => handleMouseEnterBox(findingID, matchedLesionId)}
                  onMouseLeave={handleMouseLeaveBox}
                  onContextMenu={(e) =>
                    tabsIndex === 2 &&
                    handleContextMenu(e, findingID, findingClass, lesionModule, findingIndex)
                  }
                  className={`${classes.bounding_box_diagnose} ${shouldScale ? classes.breathingBox : ''}`}
                  style={{
                    borderStyle: lesionModule === 'microcalc' ? 'dotted' : 'solid',
                    borderRadius: lesionModule === 'microcalc' ? '30%' : '0',
                    borderColor: isMissedFinding(finding)
                      ? `${theme.palette.diagnosticsClasses.priorFinding}A0`
                      : color || 'cyan',
                    left: `${100 * x}%`,
                    top: `${100 * y}%`,
                    width: `${100 * w}%`,
                    height: `${100 * h}%`,
                    boxShadow: isPrimaryHover
                      ? '0px 0px 7px 2px #1eaaaa'
                      : isMissedFinding(finding)
                        ? `#425f5f 0px 0px 1px 1px`
                        : boxShadow,
                  }}
                />
              </Tooltip>
            </div>
          );
        })}
      </>

      {/* Unassigned boxes */}
      {unassignedBoxes}

      {/* Canvas for drawing */}
      {!contextMenu && (
        <canvas
          key={`overlays-${proj_name}`}
          id={`canvas-${proj_name}`}
          className={classes.canvas_overlay}
          ref={canvasRef}
          width={imgSize[0]}
          height={imgSize[1]}
          onMouseDown={diagnosticsInRevisionMode && !contextMenu ? handleStartDrawingRectangle : undefined}
          onMouseMove={handleDrawingRect}
          onMouseUp={handleStopDrawingRectangle}
          style={{
            pointerEvents: contextMenu ? 'none' : 'auto',
            opacity:
              (tabsIndex === 0 || tabsIndex === 2) && presentation_lesions?.includes('overlay') ? 0.5 : 1,
          }}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          contextMenu={contextMenu}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          handleMenuItemClick={handleMenuItemClickWrapper}
          classes={classes}
          theme={theme}
          t={t}
          diagnostics_microcalc_module={diagnostics_microcalc_module}
          diagnostics_opacities_module={diagnostics_opacities_module}
          closeMenu={closeMenu}
        />
      )}
    </div>
  );
};

export default React.memo(DiagnoseOverlays);
